import { useState } from "react";
import { ChevronDown, ChevronRight, Trophy } from "lucide-react";
import { LeaderboardPlayerData } from "../../models/leaderboard";
import { TeamHeader } from "../teams/TeamHeader";
import { PlayerRecord } from "../PlayerRecord";
import { MatchSetupConfig, Flight } from "../../models/match";

interface KnockoutMatchLeaderboardProps {
  match: MatchSetupConfig;
  players: LeaderboardPlayerData[];
}

interface TeamPairMatches {
  teamPairId: string;
  team1: { id: string; name: string; logo?: string; totalPoints: number };
  team2: { id: string; name: string; logo?: string; totalPoints: number };
  matches: Array<{
    flight: Flight;
    player1: LeaderboardPlayerData;
    player2: LeaderboardPlayerData;
  }>;
}

export function KnockoutMatchLeaderboard({
  match,
  players,
}: KnockoutMatchLeaderboardProps) {
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());

  // Create unique team pairs and their matches from flights
  const teamPairMatches = match.flights?.reduce<Record<string, TeamPairMatches>>((acc, flight) => {
    const player1 = players.find((p) => p.playerId === flight.playerId);
    const player2 = players.find((p) => p.playerId === flight.markPlayerId);

    if (player1 && player2) {
      // Ensure consistent team pair ordering to avoid duplicates
      const [team1Id, team2Id] = [flight.teamId, flight.markTeamId].sort();
      const [sortedPlayer1, sortedPlayer2] = team1Id === flight.teamId 
        ? [player1, player2] 
        : [player2, player1];
      
      const teamPairId = `${team1Id}-${team2Id}`;

      if (!acc[teamPairId]) {
        acc[teamPairId] = {
          teamPairId,
          team1: {
            id: team1Id,
            name: sortedPlayer1.playerForTeamName || "Unknown Team",
            logo: sortedPlayer1.playerForTeamLogo,
            totalPoints: 0,
          },
          team2: {
            id: team2Id,
            name: sortedPlayer2.playerForTeamName || "Unknown Team",
            logo: sortedPlayer2.playerForTeamLogo,
            totalPoints: 0,
          },
          matches: [],
        };
      }

      // Only add unique player pairs
      const pairExists = acc[teamPairId].matches.some(
        match => 
          (match.player1.playerId === sortedPlayer1.playerId && match.player2.playerId === sortedPlayer2.playerId) ||
          (match.player1.playerId === sortedPlayer2.playerId && match.player2.playerId === sortedPlayer1.playerId)
      );

      if (!pairExists) {
        acc[teamPairId].matches.push({ 
          flight, 
          player1: sortedPlayer1, 
          player2: sortedPlayer2 
        });

        // Update team points based on player results
        const result = sortedPlayer1.matchResult?.find(
          (r) => r.opponentPlayerId === sortedPlayer2.playerId
        );
        if (result) {
          acc[teamPairId].team1.totalPoints += sortedPlayer1.totalPoints || 0;
          acc[teamPairId].team2.totalPoints += sortedPlayer2.totalPoints || 0;
        }
      }
    }
    return acc;
  }, {}) || {};

  // Helper function to find tiebreaker matches for a team pair
  const findTiebreakers = (teamPair: TeamPairMatches) => {
    return match.tieBreakers?.filter(tb => {
      const tbTeamIds = new Set(tb.teamIds);
      return tbTeamIds.has(teamPair.team1.id) && tbTeamIds.has(teamPair.team2.id);
    }) || [];
  };

  const toggleTeam = (teamPairId: string) => {
    const newExpanded = new Set(expandedTeams);
    if (newExpanded.has(teamPairId)) {
      newExpanded.delete(teamPairId);
    } else {
      newExpanded.add(teamPairId);
    }
    setExpandedTeams(newExpanded);
  };

  return (
    <div className="space-y-6">
      {Object.values(teamPairMatches).map((teamPair) => (
        <div
          key={teamPair.teamPairId}
          className="border rounded-lg dark:border-gray-600 bg-white dark:bg-gray-800 shadow-sm"
        >
          <button
            onClick={() => toggleTeam(teamPair.teamPairId)}
            className="w-full p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
          >
            <div className="flex items-center justify-center gap-4">
              <div className="flex-1 flex justify-end items-center gap-2">
                {teamPair.team1.totalPoints > teamPair.team2.totalPoints && (
                  <Trophy className="w-5 h-5 text-yellow-500" />
                )}
                <TeamHeader
                  team={{
                    id: teamPair.team1.id,
                    name: teamPair.team1.name,
                    logo: teamPair.team1.logo || "",
                    members: [],
                    description: "",
                    seasonId: "",
                    loweredStrippedName: teamPair.team1.name.toLowerCase(),
                    lastModifiedDateTime: new Date().toISOString(),
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-gray-500 dark:text-gray-400">
                  VS
                </span>
                {expandedTeams.has(teamPair.teamPairId) ? (
                  <ChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                )}
              </div>
              <div className="flex-1 flex items-center gap-2">
                <TeamHeader
                  team={{
                    id: teamPair.team2.id,
                    name: teamPair.team2.name,
                    logo: teamPair.team2.logo || "",
                    members: [],
                    description: "",
                    seasonId: "",
                    loweredStrippedName: teamPair.team2.name.toLowerCase(),
                    lastModifiedDateTime: new Date().toISOString(),
                  }}
                />
                {teamPair.team2.totalPoints > teamPair.team1.totalPoints && (
                  <Trophy className="w-5 h-5 text-yellow-500" />
                )}
              </div>
            </div>
          </button>

          {expandedTeams.has(teamPair.teamPairId) && (
            <div className="border-t dark:border-gray-600 divide-y dark:divide-gray-600">
              {/* Main matches */}
              {teamPair.matches.map(({ player1, player2 }) => {
                const result = player1.matchResult?.find(
                  (r) => r.opponentPlayerId === player2.playerId
                );

                const resultStyle = result
                  ? result.matchResult?.indexOf('win') > -1
                    ? "bg-green-100 text-green-800 dark:bg-green-800/30 dark:text-green-200 ring-green-600/20 dark:ring-green-500/30"
                    : result.matchResult === "Lost"
                    ? "bg-red-100 text-red-800 dark:bg-red-800/30 dark:text-red-200 ring-red-600/20 dark:ring-red-500/30"
                    : "bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-200 ring-gray-600/20 dark:ring-gray-500/30"
                  : "";

                return (
                  <div
                    key={`${player1.id}-${player2.id}`}
                    className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
                  >
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center justify-center gap-4">
                        <div className="flex-1 flex flex-col items-end">
                          <div className="flex items-center gap-2">
                            {result?.matchResult === "Win" && (
                              <Trophy className="w-4 h-4 text-yellow-500" />
                            )}
                            <PlayerRecord
                              member={player1}
                              showHandicapAndMsl={false}
                            />
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {player1.playerForTeamName}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            VS
                          </span>
                        </div>
                        <div className="flex-1 flex flex-col items-start">
                          <div className="flex items-center gap-2">
                            <PlayerRecord
                              member={player2}
                              showHandicapAndMsl={false}
                            />
                            {(result?.matchResult || '').indexOf('Lose') > -1 && (
                              <Trophy className="w-4 h-4 text-yellow-500" />
                            )}
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {player2.playerForTeamName}
                          </span>
                        </div>
                      </div>

                      {result && (
                        <div className="flex justify-center items-center gap-2">
                          <span
                            className={`px-3 py-1 rounded-md ring-1 text-sm font-medium ${resultStyle}`}
                          >
                            {result.matchResult === 'Win' 
                                ? `won by ${player1.firstName} ${player1.lastName}`
                                : (result.matchResult || '').indexOf('Lose') > -1 
                                    ? `won by ${player2.firstName} ${player2.lastName}` 
                                    : result.matchResult}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Tiebreaker matches */}
              {findTiebreakers(teamPair).map((tiebreaker, index) => (
                <div key={tiebreaker.id} className="divide-y dark:divide-gray-600">
                  <div className="p-2 bg-gray-50 dark:bg-gray-700/50">
                    <p className="text-sm font-medium text-center text-gray-600 dark:text-gray-300">
                      Tiebreaker {index + 1}
                    </p>
                  </div>
                  {tiebreaker.flights?.map((flight) => {
                    const tbPlayer1 = players.find((p) => p.playerId === flight.playerId);
                    const tbPlayer2 = players.find((p) => p.playerId === flight.markPlayerId);
                    
                    if (!tbPlayer1 || !tbPlayer2) return null;

                    const result = tbPlayer1.matchResult?.find(
                      (r) => r.opponentPlayerId === tbPlayer2.playerId
                    );

                    const resultStyle = result
                      ? result.matchResult?.indexOf('win') > -1
                        ? "bg-green-100 text-green-800 dark:bg-green-800/30 dark:text-green-200 ring-green-600/20 dark:ring-green-500/30"
                        : result.matchResult === "Lost"
                        ? "bg-red-100 text-red-800 dark:bg-red-800/30 dark:text-red-200 ring-red-600/20 dark:ring-red-500/30"
                        : "bg-gray-100 text-gray-800 dark:bg-gray-700/50 dark:text-gray-200 ring-gray-600/20 dark:ring-gray-500/30"
                      : "";

                    return (
                      <div
                        key={`${flight.playerId}-${flight.markPlayerId}`}
                        className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
                      >
                        <div className="flex flex-col gap-4">
                          <div className="flex items-center justify-center gap-4">
                            <div className="flex-1 flex flex-col items-end">
                              <div className="flex items-center gap-2">
                                {result?.matchResult === "Win" && (
                                  <Trophy className="w-4 h-4 text-yellow-500" />
                                )}
                                <PlayerRecord
                                  member={tbPlayer1}
                                  showHandicapAndMsl={false}
                                />
                              </div>
                              <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {tbPlayer1.playerForTeamName}
                              </span>
                            </div>
                            <div className="flex items-center">
                              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                VS
                              </span>
                            </div>
                            <div className="flex-1 flex flex-col items-start">
                              <div className="flex items-center gap-2">
                                <PlayerRecord
                                  member={tbPlayer2}
                                  showHandicapAndMsl={false}
                                />
                                {(result?.matchResult || '').indexOf('Lose') > -1 && (
                                  <Trophy className="w-4 h-4 text-yellow-500" />
                                )}
                              </div>
                              <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {tbPlayer2.playerForTeamName}
                              </span>
                            </div>
                          </div>

                          {result && (
                            <div className="flex justify-center items-center gap-2">
                              <span className={`px-3 py-1 rounded-md ring-1 text-sm font-medium ${resultStyle}`}>
                                {result.matchResult === 'Win' 
                                    ? `won by ${tbPlayer1.firstName} ${tbPlayer1.lastName}`
                                    : (result.matchResult || '').indexOf('Lose') > -1 
                                        ? `won by ${tbPlayer2.firstName} ${tbPlayer2.lastName}` 
                                        : result.matchResult}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
